import { formatPercent } from './format.ts';

/**
 * The ONE place a model-mix share becomes both a strip width and a
 * printed percent.
 *
 * It exists because those were two independent paths before (#53): the
 * strip widened any segment to a 2% floor "so a sliver stays visible",
 * the row printed `Intl` at one decimal, and nothing reconciled them.
 * Measured on a real member view, `grok-5` held 1.2% of the window,
 * was drawn at 2%, and printed `1.2%` — and because the strip is a
 * flex row whose children shrink by default, the 0.76pp of overflow
 * was taken back out of EVERY segment: `claude-haiku-4-5`, which never
 * touched the floor, printed `78.7%` and drew `78.12%`. A floor on one
 * segment silently mis-drew all six. On dev today the same fixture
 * shape declares 104.9% and hands three segments a visible bar for a
 * share of exactly zero.
 *
 * Two rules follow, and they are the whole module:
 *
 * 1. **The strip is exact.** A segment's width is its own share, never
 *    widened, and the declared widths never exceed 100%, so the browser
 *    has nothing to re-apportion (`AdminPanels.tsx` pins the segments
 *    `shrink-0` to make that structural rather than arithmetic luck).
 *    Declared width therefore equals drawn width, for every segment,
 *    always.
 *
 *    There is no width floor because a conserved strip cannot have one:
 *    the width handed to a sliver comes out of its neighbours, so the
 *    floor buys one segment's visibility with every other segment's
 *    accuracy. Visibility is not lost by dropping it — DESIGN.md §2
 *    already makes the labelled ROW the carrier ("the strip is read
 *    through the legend below it"), and every model in the strip has
 *    one, with its own swatch, token counts and percent. A share too
 *    small to see is then drawn as what it is — negligible — instead of
 *    being magnified twentyfold and dragging the rest of the row with
 *    it.
 *
 * 2. **The printed percents add up to what the strip draws.** Rounding
 *    each row on its own put the column anywhere in ±0.3pp of the
 *    strip's total (99.90% and 100.10% were both measured), so the
 *    percents are apportioned by largest remainder against the total
 *    of the shares as sent. That total is NOT forced to 100%: a server
 *    whose shares fall short of the window must keep showing the
 *    shortfall (#48 was hidden by exactly that kind of self-consistent
 *    total), so the target is the rounded sum of the input, whatever it
 *    is. Each row still lands within one 0.1pp step of its own share.
 *
 * A non-zero share too small for one decimal prints `<0.1%` rather than
 * `0%`: `0%` is reserved for a segment that draws nothing, so "there is
 * something on the strip" and "the number says zero" can never be on
 * screen together.
 */

/** Digits of a printed share — the resolution the apportionment works in. */
const UNITS_PER_PERCENT = 10;

/** One segment of the strip and the percent its row prints, from one pass. */
export interface MixShare<T> {
  /** Whatever the caller handed in, carried through so no index lookup pairs them back up. */
  readonly item: T;
  /** CSS width for the strip segment. Exact; never widened to a floor. */
  readonly width: string;
  /** Percent for the row, apportioned across the whole strip. */
  readonly percentText: string;
}

/**
 * Widths and percents for one strip, given every item on it in render
 * order (named models first, then the aggregate remainder if any).
 *
 * Shares arrive as the contract's 0..1 fractions. Anything not a
 * finite, positive number is treated as zero rather than propagated:
 * `NaN%` is not a width, it is a segment the browser silently drops.
 */
export function mixShares<T>(
  items: readonly T[],
  shareOf: (item: T) => number,
): readonly MixShare<T>[] {
  const percents = items.map((item) => {
    const share = shareOf(item);
    return Number.isFinite(share) && share > 0 ? share * 100 : 0;
  });
  const total = percents.reduce((sum, percent) => sum + percent, 0);
  // A strip cannot be more than full. Shares summing past 1 is a server
  // fault, but clipping the overflow would delete the last segment and
  // letting flex absorb it would mis-draw all of them, so it is scaled
  // down as a set — the one case where a width is not the raw share.
  const scale = total > 100 ? 100 / total : 1;
  const scaled = percents.map((percent) => percent * scale);
  const units = apportion(scaled);
  return items.map((item, index) => {
    const percent = scaled[index] ?? 0;
    return {
      item,
      width: `${percent}%`,
      percentText: percentText(percent, units[index] ?? 0),
    };
  });
}

/**
 * Largest-remainder apportionment of `percents` into whole 0.1pp units.
 *
 * The target is the rounded total of the input, so the result carries
 * the input's own sum rather than a hardcoded 100.
 */
function apportion(percents: readonly number[]): readonly number[] {
  const units = percents.map((percent) => percent * UNITS_PER_PERCENT);
  const floors = units.map((unit) => Math.floor(unit));
  const target = Math.round(units.reduce((sum, unit) => sum + unit, 0));
  const spare = target - floors.reduce((sum, floor) => sum + floor, 0);
  // Ties go to the earlier segment: the list is ordered by size, so the
  // spare unit lands on the bigger share, where it is relatively smaller.
  const bumped = new Set(
    units
      .map((unit, index) => ({ index, remainder: unit - Math.floor(unit) }))
      .sort((a, b) => b.remainder - a.remainder || a.index - b.index)
      .slice(0, Math.max(0, spare))
      .map((entry) => entry.index),
  );
  return floors.map((floor, index) => (bumped.has(index) ? floor + 1 : floor));
}

/**
 * `0%` only for a segment that draws nothing; anything on the strip but
 * under one decimal reads as less-than, built from the same formatter
 * so the panel has exactly one percent notation.
 */
function percentText(percent: number, units: number): string {
  if (units === 0 && percent > 0) return `<${formatPercent(1 / (100 * UNITS_PER_PERCENT))}`;
  return formatPercent(units / (100 * UNITS_PER_PERCENT));
}
